/*
  Customer photos for the PDP proof blocks.

  Dev notes §2. Blocks 1, 3 and 5 want a photo from a 5-star, published review
  with an image. Liquid cannot reach the Yotpo API, so this runs client-side.

  An uploaded image is treated as a deliberate choice and is never overridden.
  Yotpo only fills blocks that were left empty:

    block has an uploaded image                     → keep it, skip the pull
    empty, product has >= threshold photos          → use the product's own
    empty, fewer than that                          → leave it quote-only

  (The spec's waterfall put Yotpo above the upload. Reversed on request: an
  image chosen for a block should not disappear because that product happens
  to have crossed the photo threshold.)

  Deliberately an upgrade, never a dependency: the fallback is already in the
  markup when this runs, and a failed or slow request changes nothing. A block
  with no fallback ships with its media container hidden and is only revealed
  once there is a real photo, so no empty slot is ever painted.

  The response is cached in sessionStorage — the notes call for not querying
  Yotpo on every page load, and this is as far as a theme can go without a
  server-side job.
*/
(function () {
  var CACHE_PREFIX = 'as-yotpo-photos:';
  var CACHE_TTL_MS = 30 * 60 * 1000;
  /*
    150, not 50.

    The threshold counts reviews that are BOTH 5-star and carry a photo, but
    the API returns reviews newest-first — so a page size of 50 samples only
    the most recent 50 and counts the qualifying ones inside that window.

    Measured on Queen Locs in Naturel 24" (5046370271367):

      per_page=50   ->  50 reviews  ->   5 qualifying
      per_page=100  -> 100 reviews  ->  44 qualifying
      per_page=150  -> 132 reviews  ->  72 qualifying

    The product had 72 usable photos and the section rendered quote-only
    because the sample found 5 against a threshold of 6. The data was never
    the problem; the window was.

    132 is everything this product has, so 150 is the whole set rather than a
    bigger guess. One request either way, and the result is cached.
  */
  var PER_PAGE = 150;

  function readCache(key) {
    try {
      var raw = sessionStorage.getItem(CACHE_PREFIX + key);
      if (!raw) return null;

      var parsed = JSON.parse(raw);
      if (!parsed || Date.now() - parsed.t > CACHE_TTL_MS) return null;
      return parsed.photos;
    } catch (e) {
      // Private browsing, quota, or malformed entry — treat as a cache miss.
      return null;
    }
  }

  function writeCache(key, photos) {
    try {
      sessionStorage.setItem(CACHE_PREFIX + key, JSON.stringify({ t: Date.now(), photos: photos }));
    } catch (e) {
      /* Caching is an optimisation; failing to cache must not break the pull. */
    }
  }

  /*
    Yotpo returns an image on `images_data`, and review tags land in different
    places depending on how the account is configured, so collect whatever is
    present rather than assuming one shape.
  */
  function extractPhotos(reviews, minScore) {
    var photos = [];
    if (!Array.isArray(reviews)) return photos;

    /*
      The original rule was "5 star only". That turned out to be expensive:
      on several products the 4-star reviews carry perfectly good photos, and
      excluding them dropped the count under the threshold so the section fell
      back to quote-only. A 4-star review is still someone recommending the
      product.

      Kept as a floor rather than an exact match so the setting can be raised
      back to 5, or lowered further, without touching this file.
    */
    var floor = Number(minScore) || 5;

    reviews.forEach(function (review) {
      if (!review || Number(review.score) < floor) return;

      var images = review.images_data;
      if (!Array.isArray(images) || !images.length) return;

      var image = images[0];
      var url = image.original_url || image.image_url || image.thumb_url;
      if (!url) return;

      // Protocol-relative URLs are common in this API.
      if (url.indexOf('//') === 0) url = 'https:' + url;

      var tags = [];
      if (Array.isArray(review.custom_fields)) {
        review.custom_fields.forEach(function (field) {
          if (field && field.value) tags.push(String(field.value).toLowerCase());
        });
      }
      if (Array.isArray(review.tags)) {
        review.tags.forEach(function (tag) {
          tags.push(String(tag && tag.name ? tag.name : tag).toLowerCase());
        });
      }

      photos.push({
        id: review.id,
        url: url,
        tags: tags,
        author: review.user && review.user.display_name ? review.user.display_name : ''
      });
    });

    return photos;
  }

  function fetchPhotos(appKey, productId, minScore) {
    /*
      minScore is part of the cache key: two sections on one page could ask for
      different floors, and the second must not be served the first's answer.
    */
    var cacheKey = appKey + ':' + productId + ':' + minScore;
    var cached = readCache(cacheKey);
    if (cached) return Promise.resolve(cached);

    /*
      sort AND direction ARE NOT OPTIONAL.

      Without them this endpoint returns an empty set for some products, while
      the identical URL with sort=date returns the full list. Measured on
      Autumn Leaves Boho Goddess Locs (1516810305581):

        per_page=150&page=1                          ->  0 reviews,  0 usable
        per_page=150&page=1&sort=date                -> 57 reviews, 37 usable
        per_page=30&page=1&sort=date&direction=desc  -> 16 reviews,  7 usable

      Other products answer the unsorted call fine, which is why this went
      unnoticed: the section simply rendered quote-only and looked like a
      product with too few photos. The reviews hub never hit it because it has
      always sent sort and direction.

      Being explicit also makes the page size meaningful: PER_PAGE only samples
      a window, so it matters that the window is the newest reviews rather than
      whatever order the API happens to return.
    */
    var url =
      'https://api-cdn.yotpo.com/v1/widget/' +
      encodeURIComponent(appKey) +
      '/products/' +
      encodeURIComponent(productId) +
      '/reviews.json?per_page=' +
      PER_PAGE +
      '&page=1&sort=date&direction=desc';

    return fetch(url)
      .then(function (response) {
        if (!response.ok) return null;
        return response.json();
      })
      .then(function (data) {
        var reviews = data && data.response ? data.response.reviews : null;
        var photos = extractPhotos(reviews, minScore);
        writeCache(cacheKey, photos);
        return photos;
      })
      .catch(function () {
        return null;
      });
  }

  function paint(slot, photo) {
    var img = document.createElement('img');
    img.loading = 'lazy';
    img.alt = '';
    img.src = photo.url;

    /*
      Only ever called on an empty slot, so a dead Yotpo URL must re-hide it
      rather than paint a broken image — which the notes explicitly rule out.
    */
    img.addEventListener('error', function () {
      slot.setAttribute('hidden', '');
    });

    slot.appendChild(img);
    slot.removeAttribute('hidden');
  }

  function assign(root, photos) {
    // An uploaded image is a deliberate choice — leave those blocks alone.
    var slots = Array.prototype.slice
      .call(root.querySelectorAll('[data-proof-media]'))
      .filter(function (slot) {
        return !slot.querySelector('img');
      });

    if (!slots.length) return;

    var pool = photos.slice();
    var used = [];

    // Tagged slots pick first, so a tagged photo is never used up by a slot
    // that would have accepted anything.
    var ordered = slots
      .filter(function (slot) {
        return slot.dataset.yotpoTag;
      })
      .concat(
        slots.filter(function (slot) {
          return !slot.dataset.yotpoTag;
        })
      );

    ordered.forEach(function (slot) {
      if (!pool.length) return;

      var index = 0;
      var tag = (slot.dataset.yotpoTag || '').toLowerCase();

      if (tag) {
        for (var i = 0; i < pool.length; i++) {
          if (
            pool[i].tags.some(function (candidate) {
              return candidate.indexOf(tag) !== -1;
            })
          ) {
            index = i;
            break;
          }
        }
      }

      // Splice, so each block gets a different face.
      var chosen = pool.splice(index, 1)[0];
      paint(slot, chosen);
      used.push(chosen.id);
    });

    /*
      Tell the reviews hub which photos are now spoken for, so the same face
      does not appear twice on one page. Claiming after painting rather than
      before means a photo that fails to load was still claimed — a small
      price for not having to unpick it, and the hub simply shows one fewer.
    */
    if (used.length && window.AsYotpoClaims) window.AsYotpoClaims.claim(used);
  }

  function init(root) {
    if (root.dataset.proofImagesInit === '1') return;
    root.dataset.proofImagesInit = '1';

    if (root.dataset.yotpoImages !== 'true') return;

    var appKey = root.dataset.yotpoAppKey;
    var productId = root.dataset.productId;
    var minPhotos = parseInt(root.dataset.minPhotos, 10);
    if (isNaN(minPhotos)) minPhotos = 6;

    var minScore = parseInt(root.dataset.minScore, 10);
    if (isNaN(minScore)) minScore = 4;
    if (!appKey || !productId) return;

    /*
      Review ids whose photos the merchant has withheld. Strings on both sides:
      Yotpo sends numbers, the setting stores text.
    */
    var hidden = (root.dataset.hiddenPhotoReviews || '')
      .split(',')
      .map(function (id) {
        return id.trim();
      })
      .filter(Boolean);

    fetchPhotos(appKey, productId, minScore).then(function (photos) {
      /*
        Filtered AFTER the cache and BEFORE the threshold, deliberately.

        After the cache because the cache is keyed on app key and product, and
        is shared with any other section on the page — one section's exclusion
        must not poison what another reads.

        Before the threshold because a withheld photo is not available, so it
        must not prop up the count. Excluding the last one that put a product
        over the line should drop it back to the curated fallback, not leave a
        gap where that photo would have been.
      */
      if (photos && hidden.length) {
        photos = photos.filter(function (photo) {
          return hidden.indexOf(String(photo.id)) === -1;
        });
      }

      // Below the threshold the pull repeats faces and looks sparse, so the
      // curated fallback already in the markup is the better answer.
      if (!photos || photos.length < minPhotos) return;
      assign(root, photos);
    });
  }

  function start() {
    document.querySelectorAll('[id^="ProofBlocks-"]').forEach(init);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', start);
  } else {
    start();
  }

  // Theme editor re-renders the section without a page load.
  document.addEventListener('shopify:section:load', function (event) {
    var root = event.target.querySelector('[id^="ProofBlocks-"]');
    if (root) init(root);
  });
})();
